import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import {
  setIsAuthenticated,
  setAccessToken,
  setUser,
  setSessionExpired,
} from "../Redux/Reducers/appState";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { DrawService } from "../services/draw/DrawService";
import { WalletService } from "../services/wallet/wallet";
import { TicketService } from "../services/Ticket/Ticket";

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.appState.user);
  const sessionExpired = useSelector((state) => state.appState.sessionExpired);

  const [walletVisible, setWalletVisible] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [nextDraw, setNextDraw] = useState(null);
  const [lastActivity, setLastActivity] = useState(new Date().toLocaleString());

  useEffect(() => {
    if (!sessionExpired) return;

    dispatch(setUser(null));
    dispatch(setAccessToken(null));
    dispatch(setIsAuthenticated(false));

    setTimeout(() => {
      navigate("/login");
    }, 500);
  }, [dispatch, navigate, sessionExpired]);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [walletRes, ticketRes, drawRes] = await Promise.all([
          WalletService.getBalance(),
          TicketService.getUserTickets(),
          DrawService.getNextDraw(),
        ]);

        if (
          walletRes?.message === "Invalid or expired token" ||
          ticketRes?.message === "Invalid or expired token"
        ) {
          dispatch(setSessionExpired(true));
          return;
        }

        setWalletBalance(Number(walletRes?.balance || 0));
        setTickets(Array.isArray(ticketRes) ? ticketRes : ticketRes?.data || []);

        const resolvedDraw =
          drawRes?.data && !Array.isArray(drawRes.data) ? drawRes.data : drawRes;
        setNextDraw(resolvedDraw?.draw_date ? resolvedDraw : null);
        setLastActivity(new Date().toLocaleString());
      } catch (error) {
        console.log("Dashboard fetch error:", error);
      }
    };

    loadDashboard();

    const refreshInterval = setInterval(loadDashboard, 30000);
    return () => clearInterval(refreshInterval);
  }, [dispatch]);

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        background: "linear-gradient(180deg,#001f3f,#003366,#0b2a4a)",
        color: "white",
      }}
    >
      <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
        Welcome{user?.first_name ? `, ${user.first_name}` : ""}
      </Typography>

      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(6px)",
          mb: 3,
        }}
      >
        <Typography fontSize={18} sx={{ color: "white", opacity: 0.8 }}>
          Wallet Balance
        </Typography>

        <Stack direction="row" spacing={1} mt={1} alignItems="center">
          <Typography variant="h4" fontWeight={800} sx={{ color: "white" }}>
            {walletVisible ? `₦${walletBalance.toLocaleString()}` : "******"}
          </Typography>

          <IconButton
            sx={{ color: "#ffb300" }}
            onClick={() => setWalletVisible(!walletVisible)}
          >
            {walletVisible ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" mt={2}>
          <AccessTimeIcon sx={{ color: "#ffb300" }} />
          <Typography fontWeight={600} sx={{ color: "white" }}>
            Next Draw: {nextDraw?.draw_code || "--"}
          </Typography>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          sx={{
            mt: 3,
            bgcolor: "#5fd068",
            color: "#003300",
            fontWeight: 800,
          }}
          onClick={() => navigate("/buy")}
        >
          Buy Ticket
        </Button>
      </Card>

      <Stack spacing={2}>
        <DashboardCard
          title="My Tickets"
          value={`${tickets.length} Tickets`}
          icon={<ConfirmationNumberIcon sx={{ fontSize: 40, color: "#4da3ff" }} />}
          onClick={() => navigate("/account/authmenu/account/tickets")}
        />

        <DashboardCard
          title="Live Draw"
          subtitle="Watch Now"
          icon={<PlayCircleFilledWhiteIcon sx={{ fontSize: 40, color: "#ffb300" }} />}
          onClick={() => navigate("/live")}
        />

        <DashboardCard
          title="Latest Results"
          subtitle="View"
          icon={<CheckCircleIcon sx={{ fontSize: 40, color: "#5fd068" }} />}
          onClick={() => navigate("/winners")}
        />

        <DashboardCard
          title="Payout Status"
          subtitle={
            <Chip
              label="Verified"
              icon={<VerifiedIcon />}
              sx={{
                bgcolor: "rgba(76,175,80,0.2)",
                color: "#81c784",
              }}
            />
          }
          icon={<AccountBalanceWalletIcon sx={{ fontSize: 40, color: "#ffb300" }} />}
          onClick={() => navigate("/payouts")}
        />
      </Stack>

      <Box
        sx={{
          mt: 4,
          p: 2,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.07)",
        }}
      >
        <Typography fontSize={14} sx={{ opacity: 0.7, color: "white" }}>
          Last activity: <strong>{lastActivity}</strong>
        </Typography>
      </Box>
    </Box>
  );
}

const DashboardCard = ({ title, subtitle, value, icon, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      p: 3,
      borderRadius: 2,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      backdropFilter: "blur(4px)",
      cursor: "pointer",
      "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>
          {title}
        </Typography>

        {value && (
          <Typography sx={{ opacity: 0.8, mt: 0.5, color: "white" }}>
            {value}
          </Typography>
        )}

        {subtitle && (
          <Typography sx={{ opacity: 0.8, mt: 0.5, color: "white" }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {icon}
    </Stack>
  </Card>
);
